import { ButcherHeader } from '@components/ButcherHeader';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { useCreditAmount } from '@contexts/CreditAmountContext';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBreadCrumbSteps } from '@helpers/Constants';
import { FrontendRoutes } from '@helpers/Constants/Navigation';
import { CustomerDetails } from '@helpers/Models';
import { CustomerOrderService } from '@services/CustomerService';
import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

const NewOrderLayout: React.FC = () => {
  const navigate = useNavigate();
  const { showConfirmationToast } = UseConfirmationToast();
  const location = useLocation();
  const isCartScreen = location.pathname === '/create-order/cart';
  const isEditOrder = location.state?.from?.orderDetails ?? false;

  const [customerDetails, setCustomerDetails] =
    useState<CustomerDetails | null>(null);
  // const [creditAmount, setCreditAmount] = useState();
  const { creditAmount, setCreditAmount } = useCreditAmount();
  const { handlePlaceOrder } = useCreditAmount();

  /**
   * Effect hook to fetch and set customer details from local storage.
   *
   * This effect runs once when the component mounts or when the `customerId` changes.
   * It retrieves the customer details from the local storage and updates the `customerDetails` state.
   * If no customer details are found, it redirects the user to the customer list page.
   */
  useEffect(() => {
    const details = localStorage.getItem('customerDetails');

    if (details) {
      setCustomerDetails(JSON.parse(details));
    }
  }, []);

  /**
   * Synchronizes the component state with persistent storage on mount or mode change.
   * * This hook performs a multi-layered lookup:
   * 1. Restores `customerDetails` from local storage.
   * 2. Determines the correct storage bucket ('editOrderData' vs 'orderData').
   * 3. Identifies the specific customer and the specific item index (`currentCustomizingId`) being edited.
   * 4. Extracts and sets the `creditAmount` for the currently active species/item.
   * * @listens isEditOrder - Re-runs if the user toggles between creating and editing an order.
   */
  useEffect(() => {
    const details = localStorage.getItem('customerDetails');
    if (details) {
      setCustomerDetails(JSON.parse(details));
    }

    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const raw = localStorage.getItem(storageKey);

    if (raw) {
      const all = JSON.parse(raw);
      const customerId = localStorage.getItem('customerId');
      const customerOrder = all[customerId ?? 1];

      if (isCartScreen) {
        // Cart screen - sum of all species credit amount
        const totalCredit =
          customerOrder?.species?.reduce(
            (total: number, species: any) =>
              total + (species.creditAmount ?? 0),
            0,
          ) ?? 0;
        setCreditAmount(totalCredit);
      } else {
        // Species details - only current species credit amount
        const currentCustomizingId = Number(
          localStorage.getItem('currentCustomizingId') ?? '0',
        );
        const currentSpecies = customerOrder?.species?.[currentCustomizingId];
        setCreditAmount(currentSpecies?.creditAmount ?? 0);
      }
    }
  }, [isEditOrder]);

  /**
   * Handles discarding the current order based on the mode (edit or create).
   *
   * - In edit mode:
   *   - Clears stored edit order data from localStorage.
   *   - Resets the current customization state.
   *   - Navigates back to the all orders list.
   *
   * - In create mode:
   *   - Retrieves the current customer ID.
   *   - Removes the corresponding order data from stored order data.
   *   - Updates the persisted order data via CustomerOrderService.
   *   - Clears related customization and species data from localStorage.
   *   - Navigates back to the customer list.
   *
   * Dependencies:
   * @dependency isEditOrder - Determines whether the flow is edit or create.
   * @dependency CustomerOrderService - Handles parsing and updating stored order data.
   * @dependency navigate - Used for routing after cleanup.
   */
  const discardOrder = (): void => {
    if (isEditOrder) {
      // Edit order - remove editOrderData and go back to all orders
      localStorage.removeItem('editOrderData');
      localStorage.removeItem('currentCustomizingId');
      navigate(FrontendRoutes.ALL_ORDER_LIST);
    } else {
      // Create order - remove orderData and go to customer list
      const customerId = localStorage.getItem('customerId');
      const currentCustomerId = customerId ?? 1;

      const parsedOrderData = CustomerOrderService.getParsedOrderData();
      delete parsedOrderData[currentCustomerId];
      CustomerOrderService.setParsedOrderData(parsedOrderData);

      localStorage.removeItem('currentCustomizingId');
      localStorage.removeItem('speciesName');

      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
  };

  /**
   * Handles back navigation with conditional confirmation for unsaved changes.
   *
   * - If the user is editing an order and is not on the cart screen:
   *   - Displays a confirmation toast warning about unsaved changes.
   *   - On confirmation, discards changes and navigates to the all orders list.
   *   - On cancel, keeps the user on the current screen.
   *
   * - In all other cases:
   *   - Performs a default back navigation to the previous route.
   *
   * Dependencies:
   * @dependency isEditOrder - Indicates whether the user is editing an order.
   * @dependency isCartScreen - Determines if the current screen is the cart screen.
   * @dependency navigate - Handles routing/navigation.
   * @dependency showConfirmationToast - Displays confirmation prompt to the user.
   */
  const handleBackNavigation = () => {
    if (isEditOrder && !isCartScreen) {
      showConfirmationToast(
        'Any unsaved changes will be lost.',
        'Discard Changes',
        'delete',
        () => navigate(FrontendRoutes.ALL_ORDER_LIST),
        'Discard Changes',
        () => {},
        'Keep Editing',
      );
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="flex h-screen w-screen">
      <SideNav active={2} />
      <div className="flex-1 overflow-auto relative">
        <ButcherHeader />
        <OrderBreadcrumb
          steps={orderBreadCrumbSteps}
          customerName={`${customerDetails?.firstName} ${customerDetails?.lastName}`}
          activeItem="New Order"
          customerId={customerDetails?.customerId}
        />
        {/* Back Navigation, New Order, Customer Details and Trash */}
        <div className="p-3 pb-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleBackNavigation}
                className="text-[#43474E] hover:text-primary text-lg focus:outline-none"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>

              {/* Title */}
              <div className="text-[#49494C] flex items-center gap-2">
                {isCartScreen ? (
                  <>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.49563 16C4.08188 16 3.72917 15.8527 3.4375 15.5581C3.14583 15.2635 3 14.9094 3 14.4956C3 14.0819 3.14729 13.7292 3.44188 13.4375C3.73646 13.1458 4.09063 13 4.50438 13C4.91813 13 5.27083 13.1473 5.5625 13.4419C5.85417 13.7365 6 14.0906 6 14.5044C6 14.9181 5.85271 15.2708 5.55813 15.5625C5.26354 15.8542 4.90938 16 4.49563 16ZM13.4956 16C13.0819 16 12.7292 15.8527 12.4375 15.5581C12.1458 15.2635 12 14.9094 12 14.4956C12 14.0819 12.1473 13.7292 12.4419 13.4375C12.7365 13.1458 13.0906 13 13.5044 13C13.9181 13 14.2708 13.1473 14.5625 13.4419C14.8542 13.7365 15 14.0906 15 14.5044C15 14.9181 14.8527 15.2708 14.5581 15.5625C14.2635 15.8542 13.9094 16 13.4956 16ZM4.27083 3.5L6 7.5H12.2708L13.9792 3.5H4.27083ZM3.625 2H15.5C15.6944 2 15.8368 2.07639 15.9271 2.22917C16.0174 2.38194 16.0278 2.54167 15.9583 2.70833L13.6381 8.08042C13.5183 8.36014 13.3368 8.58333 13.0938 8.75C12.8507 8.91667 12.5764 9 12.2708 9H5.60417L4.72917 10.5H15V12H4.75C4.15278 12 3.71181 11.749 3.42708 11.2469C3.14236 10.7447 3.14583 10.2457 3.4375 9.75L4.52083 7.875L1.79167 1.5H0V0H2.77083L3.625 2Z"
                        fill="#49494C"
                      />
                    </svg>

                    <span className="font-semibold text-xl">Cart</span>
                  </>
                ) : (
                  <span className="font-semibold text-xl">
                    {isEditOrder ? 'Edit Order' : 'New Order'}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Customer Badge */}
                <div className="px-4 py-1 font-semibold rounded-full border-2 w-fit text-center bg-[#DFDFD7]">
                  {customerDetails?.firstName} {customerDetails?.lastName} | +1{' '}
                  {customerDetails?.phoneHome}
                </div>

                {/* Credit Badge - edit mode */}
                {isEditOrder && (
                  <div
                    className="inline-flex items-center gap-1 text-sm px-3 py-1 rounded-full bg-[#F8F8F8] borderw-fit"
                    title="Credit applied to customer’s account for future use."
                  >
                    <span className="text-[#49494C]">Credit:</span>
                    <span className="font-semibold">${creditAmount}</span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="shrink-0 translate-y-[1px] ml-1"
                    >
                      <circle
                        cx="8"
                        cy="8"
                        r="7"
                        stroke="#888888"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M8 4.5V8.5M8 11V11.5"
                        stroke="#888888"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </div>

            {isCartScreen ? (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handlePlaceOrder?.()}
                  className="bg-primary text-white px-4 py-2 rounded-md"
                >
                  Complete Order
                </button>
                <button
                  type="button"
                  onClick={() =>
                    showConfirmationToast(
                      'Are you sure you want to discard your Order? This action cannot be undone.',
                      'Discard Order',
                      'delete',
                      discardOrder,
                      'Discard Order',
                      undefined,
                      'Cancel',
                    )
                  }
                  className="px-4 py-2 rounded-md hover:bg-red-50 bg-[#DF1A0C0D]"
                >
                  <FontAwesomeIcon
                    icon={faTrashCan}
                    className="text-[#DF1A0C]"
                  />
                </button>
              </div>
            ) : (
              <div className="px-4 py-1 font-semibold whitespace-nowrap">
                <button
                  type="button"
                  onClick={() =>
                    showConfirmationToast(
                      'Are you sure you want to discard your Order? This action cannot be undone.',
                      'Discard Order',
                      'delete',
                      discardOrder,
                      'Discard Order',
                      undefined,
                      'Cancel',
                    )
                  }
                  className="px-4 py-2 rounded-md bg-[#DF1A0C0D]"
                >
                  <FontAwesomeIcon
                    icon={faTrashCan}
                    className="text-[#DF1A0C]"
                  />
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="p-2">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
export default NewOrderLayout;
